/**
 * Reusable assign / escalate teammates drawer.
 *
 * One component, two modes:
 *  - mode="assign"   → title "Assign", primary CTA "Save"
 *  - mode="escalate" → title "Escalate", primary CTA "Save"
=
 * Cancel-resets-state pattern: the host renders this drawer behind a gate
 * (`{isEscalateDrawerOpen && <AssignTeammatesDrawer ... />}`). When the user
 * cancels / closes / Escapes / clicks backdrop, the host flips the gate to
 * false → the drawer unmounts → all useState defaults restore on next open.
 */

import { Box, Stack, useTheme } from "@mui/material";
import { CircularLoader, Drawer, SearchInput, Tooltip } from "components";
import {
  useEscalateTicket,
  useFetchResourcesByPermissions,
  useGlobalStore,
} from "controller";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import type { TicketEscalatedMember } from "shared/types/escalations";
import { emptyStringHandler } from "utilz/helpers";
import { v4 as uuidv4 } from "uuid";
import { AddScheduleButton } from "./components/AddScheduleButton";
import { ApplySameTimeBanner } from "./components/ApplySameTimeBanner";
import { EmptyState } from "./components/EmptyState";
import { MemberRowHeader } from "./components/MemberRowHeader";
import { MemberTimeRow } from "./components/MemberTimeRow";
import {
  hasAnyDuplicateSlots,
  useIsAnyTimeRowInvalid,
  validateMemberSlots,
} from "./hooks/useTimeRowValidation";
import type {
  AssignTeammatesDrawerProps,
  ResourceRow,
  SelectedMemberEntry,
  TimeSlotDraft,
} from "./types";
import { buildAssignPayload } from "./utils/buildAssignPayload";
import { buildEscalatePayload } from "./utils/buildEscalatePayload";
import { applySlotPatch, buildNowTimeSlot } from "./utils/timeSlotDraft";

// Two slots are "matching" iff both timestamps equal AS INSTANTS (ISO
// equality is sufficient because both go through `dayjs.utc().toISOString()`
// which canonicalises to ms precision).
const slotMatches = (a: TimeSlotDraft, b: TimeSlotDraft): boolean =>
  a.startIso === b.startIso && a.endIso === b.endIso;

const slotIsNonEmpty = (slot: TimeSlotDraft | undefined): boolean => {
  if (!slot) return false;
  return slot.startIso !== null || slot.endIso !== null;
};

// Order-independent signature of the current selection (members + their slot
// start/end values). Used to enable Save only when the selection differs from
// the hydrated baseline. Members are sorted so reordering isn't a "change";
// slot order is preserved (the first slot is canonical, so it's meaningful).
const selectionSignature = (map: Map<string, SelectedMemberEntry>): string =>
  Array.from(map.values())
    .map(
      (e) =>
        `${e.resource.mapping_id}:${e.timeSlots
          .map((s) => `${s.startIso ?? ""}~${s.endIso ?? ""}`)
          .join(",")}`,
    )
    .sort()
    .join("|");

// Helpers for the `alreadyEscalatedEntries` memo. Kept at module scope so
// the memo body stays small (cyclomatic-complexity rule).
const newEntryFromEscalatedMember = (
  m: TicketEscalatedMember,
): SelectedMemberEntry => ({
  resource: {
    mapping_id: m.resource_id,
    first_name: m.first_name ?? "",
    last_name: m.last_name ?? "",
    profile_image: m.profile_image,
  },
  timeSlots: [],
});

const slotFromEscalatedMember = (
  m: TicketEscalatedMember,
): TimeSlotDraft | null => {
  if (m.start_time === null && m.end_time === null) return null;
  return {
    slotUuid: `already-${m.mapping_id}`,
    scheduleId: m.mapping_id,
    startIso: m.start_time,
    endIso: m.end_time,
    error: { start: null, end: null },
  };
};

export const AssignTeammatesDrawer = ({
  open,
  onClose,
  mode,
  customerId,
  serviceType,
  ticketId,
  alreadyEscalatedMembers,
  tableData,
  readOnly = false,
  enableScheduling = false,
  onSave,
  onEscalateSuccess,
}: AssignTeammatesDrawerProps) => {
  const title = mode === "escalate" ? "Escalate" : "Assign team members";

  // RESOURCE timezone (matches MemberTimeRow's source) — used to seed a freshly
  // added schedule row's Start/End with the resource's current wall-clock.
  const timezone = useGlobalStore((state) => state.timezone);

  // Time scheduling (per-member start/end rows, copy/delete slots, "Apply same
  // time to all" banner) is always on for escalate, and opt-in for assign via
  // `enableScheduling`. Single source of truth so every scheduling affordance
  // toggles together.
  const schedulingEnabled = mode === "escalate" || enableScheduling;

  // Local useState. The query
  // is read by the resource fetch below — server-side filtering only, no
  // client-side filter. Cancel-resets-state is preserved via host's
  // mount/unmount gate (drawer unmounts on close → useState defaults restore).
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Local selection state. Keyed by `resource.mapping_id`.
  // Value evolved to `SelectedMemberEntry { resource, timeSlots }` so each
  // member carries its own slot list (multi-slot per member supported via
  // copy icon).
  //
  // Already-escalated members are intentionally NOT seeded here — locked rule
  // ("such members must NOT be in the payload"). They render as
  // disabled-selected cards in the available list only.
  const [selectedMap, setSelectedMap] = useState<
    Map<string, SelectedMemberEntry>
  >(new Map());

  // Banner state — pure local. Dismiss is per-mount; the host's
  // mount/unmount gate clears it on drawer close.
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
  // Tracks the most recently edited member id — that member's first slot is
  // the "canonical" one we copy from when applying. Falls back to the first
  // selected member.
  const [activeMemberId, setActiveMemberId] = useState<string | null>(null);
  // Banner is offered only after the user actively edits a time in this session
  // — NOT for pre-existing divergent schedules hydrated on open (edit flow),
  // where differing times are the intended saved state, not a mistake to fix.
  // Set in `updateSlot` only; never during hydration. Per-mount (host's
  // mount/unmount gate resets it on close).
  const [hasUserEditedSlot, setHasUserEditedSlot] = useState<boolean>(false);

  // Signature of the selection as hydrated on open — the baseline Save is
  // compared against so the button enables only when something actually
  // changed. Set in the hydration effect; reset per-mount with the component.
  const baselineSignatureRef = useRef<string>("");

  // Pinned-top group, frozen at open. The members that were already
  // selected/escalated FROM THE API when the drawer opened stay pinned at the
  // top for the whole session — even after being unselected (unselecting shows
  // the unselected state IN PLACE at the top instead of dropping the row down).
  // The next open recomputes this from fresh API data, so a saved-unselected
  // member falls back to its natural list position.
  //  - `Ids`      → frozen exclusion set for the searchable list below.
  //  - `Order`    → API order the pinned rows render in.
  //  - `Snapshot` → resource snapshot so an unselected pinned member can still
  //                 render (it may not be in the current — possibly searched —
  //                 resource list).
  // Assign mode only; escalate's pinned group is the already-escalated members.
  const initialSelectedIdsRef = useRef<Set<string>>(new Set());
  const initialSelectedOrderRef = useRef<string[]>([]);
  const initialSelectedSnapshotRef = useRef<Map<string, ResourceRow>>(
    new Map(),
  );

  // escalate-mode mutation. Hook is parameterless; ticketId +
  // payload pass via mutateAsync. Dual cache invalidation
  // (["ticket", id] + ["ticket_escalations", id]) is owned by the hook
  // (controller/hooks/.../useEscalations.ts) — DO NOT duplicate here.
  const {
    mutateAsync: escalateTicketAsync,
    isLoading: isEscalateMutationLoading,
  } = useEscalateTicket();

  // Assign-mode save can be async (the host's onSave may hit an API, e.g. the
  // open-ticket "create/schedule" call). Tracks that in-flight state so the
  // Save button shows loading and stays disabled while persisting.
  const [isAssignSaving, setIsAssignSaving] = useState<boolean>(false);

  // Duplicate slots are a SAVE-TIME check, not live. Copying a row (which
  // briefly produces an identical slot the user is about to edit) must NOT
  // flash a "duplicate" error. We remember the selection signature at the
  // moment of a Save attempt that found duplicates; duplicate errors render
  // only while the selection still matches that signature. Any edit changes the
  // signature → errors hide → the next reveal is again a fresh save attempt.
  const [duplicateAttemptSignature, setDuplicateAttemptSignature] = useState<
    string | null
  >(null);

  // hydrate selectedMap from incoming data on first open.
  //
  //  - assign mode — hydrate from `tableData` (each row → 1 entry, no
  //    timeSlots in assign mode, parity with recurring drawer).
  //  - escalate mode — selectedMap stays empty on open. Already-escalated
  //    members do NOT enter selectedMap (locked rule: they must never reach
  //    the payload); they render separately via `alreadyEscalatedEntries`
  //    in the Selected section as disabled-selected cards. This applies to
  //    BOTH the normal escalate flow AND the closed-ticket readOnly view.
  //
  // Dependency on `open` only — parent passes fresh data each open cycle and
  // the host's mount/unmount gate guarantees this runs at the right time.
  useEffect(() => {
    if (!open) return;

    const next = new Map<string, SelectedMemberEntry>();

    if (mode === "assign" && tableData && tableData.length > 0) {
      // Group incoming rows by resource — `tableData` carries ONE row per
      // schedule slot, so a member with N existing schedules appears N times.
      // Each row's start/end (when present) becomes a TimeSlotDraft so existing
      // schedules show as editable time rows; rows with null start AND end carry
      // no slot (e.g. an "assign to me" row, or an assignee with no schedule).
      for (const t of tableData) {
        if (!t.resource_id) continue;
        let entry = next.get(t.resource_id);
        if (!entry) {
          entry = {
            resource: {
              mapping_id: t.resource_id,
              first_name: t.first_name,
              last_name: t.last_name,
              profile_image: t.profile_image,
            },
            timeSlots: [],
            // Backend flags an assigned-then-deactivated tech with
            // is_active === false — lock that member's schedule (DD-5433).
            isInactive: t.is_active === false,
          };
          next.set(t.resource_id, entry);
        }
        // A scheduleless assignee (start AND end null) carries NO time slot —
        // it renders as a plain selected row (no empty time row); the user adds
        // a schedule via the header AddScheduleIcon. The backend's null/null
        // row is reconciled on save by value (see diffAssignSchedules), so we
        // don't need to hold its mapping_id in a slot.
        if (t.start_time !== null || t.end_time !== null) {
          entry.timeSlots.push({
            slotUuid: uuidv4(),
            // Preserve the backend schedule id so the save diff can UPDATE or
            // DELETE this exact row instead of re-adding it.
            scheduleId: t.mapping_id ?? null,
            startIso: t.start_time,
            endIso: t.end_time,
            error: { start: null, end: null },
          });
        }
      }
    }

    setSelectedMap(next);
    baselineSignatureRef.current = selectionSignature(next);
    // Freeze the pinned-top group from the members selected at open (assign
    // mode only — escalate pins the already-escalated members instead).
    initialSelectedIdsRef.current = new Set(next.keys());
    initialSelectedOrderRef.current = Array.from(next.keys());
    const snapshot = new Map<string, ResourceRow>();
    for (const [id, entry] of next) snapshot.set(id, entry.resource);
    initialSelectedSnapshotRef.current = snapshot;
    setSearchQuery("");
    setBannerDismissed(false);
    setActiveMemberId(null);
    // Intentional: hydrate only on open transition. Parent supplies fresh data
    // for every open cycle (host's mount/unmount gate enforces this).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { data, isLoading, isError } = useFetchResourcesByPermissions({
    sortColumn: "users.first_name",
    customer_id: customerId,
    filter: [],
    serviceType,
    ...(searchQuery ? { searchQuery } : {}),
  });

  const resourceList: ResourceRow[] = useMemo(() => {
    return (data?.result ?? []) as ResourceRow[];
  }, [data]);

  //  Set of resource ids that are already escalated on this ticket.
  // `TicketEscalatedMember.resource_id` is the resource short id (matches
  // `ResourceRow.mapping_id`).
  const alreadyEscalatedSet = useMemo(
    () => new Set((alreadyEscalatedMembers ?? []).map((m) => m.resource_id)),
    [alreadyEscalatedMembers],
  );

  // Synthesize `SelectedMemberEntry` objects for already-escalated members
  // so the "Selected" section can render them as disabled-selected cards
  // (alongside the newly-selected members). Grouped by `resource_id` so a
  // member with multiple historical slots renders as one card with N rows.
  // Slots with BOTH start and end null are filtered out — they would render
  // as an empty "Start date & time → End date & time" placeholder row,
  // which makes no sense in read-only history. If a member has no non-empty
  // slots, the card renders without the time-rows section (avatar + name
  // + disabled checkbox only).
  // These entries are render-only — they are NEVER inserted into
  // `selectedMap`, so they cannot reach the payload (locked rule).
  const alreadyEscalatedEntries: SelectedMemberEntry[] = useMemo(() => {
    if (mode !== "escalate" || !alreadyEscalatedMembers?.length) return [];
    const byResource = new Map<string, SelectedMemberEntry>();
    for (const m of alreadyEscalatedMembers) {
      if (!m.resource_id) continue;
      let entry = byResource.get(m.resource_id);
      if (!entry) {
        entry = newEntryFromEscalatedMember(m);
        byResource.set(m.resource_id, entry);
      }
      const slot = slotFromEscalatedMember(m);
      if (slot) entry.timeSlots.push(slot);
    }
    return Array.from(byResource.values());
  }, [mode, alreadyEscalatedMembers]);

  // Validation aggregate — true iff any selected slot has an invalid range.
  const isAnyTimeRowInvalid = useIsAnyTimeRowInvalid(selectedMap);

  // ---- Selection mutators -------------------------------------------------

  //  toggle selection. Redesign: selecting NO LONGER seeds a
  // time row — every new selection starts with `timeSlots: []`. The user adds
  // the first (and further) rows via the header AddScheduleIcon → `addSlot`.
  // On deselect: remove the entry entirely AND, if it was the active one,
  // re-pick.
  const toggleSelection = (resource: ResourceRow) => {
    // Determine add-vs-remove from CURRENT state before mutating, so the
    // active-id update below knows which branch it is in (the setSelectedMap
    // updater can't tell setActiveMemberId what it did).
    const isCurrentlySelected = selectedMap.has(resource.mapping_id);
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(resource.mapping_id)) {
        next.delete(resource.mapping_id);
        return next;
      }
      next.set(resource.mapping_id, {
        resource,
        timeSlots: [],
      });
      return next;
    });
    setActiveMemberId((prevActive) => {
      if (isCurrentlySelected) {
        // Deselecting: clear the active id ONLY if it was this member;
        // deselecting any OTHER member must NOT make the removed member
        // "active" (that left a dangling id → wrong "apply same time" source).
        return prevActive === resource.mapping_id ? null : prevActive;
      }
      // Selecting: this member becomes the most-recently-touched (canonical).
      return resource.mapping_id;
    });
  };

  // Slot-level mutators — pure functional updates over the Map.
  //
  // Auto-fill rule: whenever the START changes, keep END strictly after it.
  // End is re-derived to start + 1h when it is either missing OR no longer
  // after the new start. "Missing" covers backend-hydrated partial rows
  // (start_time present, end_time null); "no longer after" covers the user
  // dragging Start past a previously-chosen End. A still-valid End (after the
  // new start) is preserved. dayjs.add is timezone-agnostic on UTC ISO — it
  // just shifts the instant by 3600000ms; render-time conversion handles tz.
  //
  // Times are change-only: a slot always has both Start and End once it exists
  // (seeded on add, derived here). A patch that would null/empty either field
  // is ignored — removal is delete-the-whole-row, never clear-a-field.
  const updateSlot = (
    resourceId: string,
    slotIndex: number,
    patch: Partial<Pick<TimeSlotDraft, "startIso" | "endIso">>,
  ) => {
    // Reject empty/null patches up front — change-only contract.
    if (
      ("startIso" in patch && !patch.startIso) ||
      ("endIso" in patch && !patch.endIso)
    ) {
      return;
    }
    setSelectedMap((prev) => {
      const entry = prev.get(resourceId);
      if (!entry) return prev;
      const next = new Map(prev);
      const nextSlots = entry.timeSlots.map((slot, idx) => {
        if (idx !== slotIndex) return slot;
        // Empty patches are rejected up front, so this is never null here.
        return applySlotPatch(slot, patch) ?? slot;
      });
      next.set(resourceId, { ...entry, timeSlots: nextSlots });
      return next;
    });
    setActiveMemberId(resourceId);
    // Re-show banner whenever the user edits times.
    setBannerDismissed(false);
    setHasUserEditedSlot(true);
  };

  // Append a new schedule row seeded with the resource's current time — used by
  // BOTH the header's first-add and the card-level "+" (add another). A fresh
  // uuid gives it independent edit state; scheduleId is null so it POSTs as a
  // new schedule. (Replaces the earlier per-row copy/duplicate affordance.)
  const addSlot = (resourceId: string) => {
    setSelectedMap((prev) => {
      const entry = prev.get(resourceId);
      if (!entry) return prev;
      const next = new Map(prev);
      next.set(resourceId, {
        ...entry,
        timeSlots: [...entry.timeSlots, buildNowTimeSlot(timezone)],
      });
      return next;
    });
  };

  const removeSlot = (resourceId: string, slotIndex: number) => {
    setSelectedMap((prev) => {
      const entry = prev.get(resourceId);
      if (!entry) return prev;
      // Delete is allowed on every row, including the last — removing it
      // returns the member to "selected, no schedule" (timeSlots: []).
      const next = new Map(prev);
      next.set(resourceId, {
        ...entry,
        timeSlots: entry.timeSlots.filter((_, idx) => idx !== slotIndex),
      });
      return next;
    });
  };

  // Header AddScheduleIcon toggle — when a member already has schedule rows,
  // clicking the (active-pill) icon clears them all back to none. Adding the
  // first row is the other half of the toggle (handled inline at the call site
  // via `addSlot`); adding FURTHER rows is the card-level "+" button.
  const clearSlots = (resourceId: string) => {
    setSelectedMap((prev) => {
      const entry = prev.get(resourceId);
      if (!entry) return prev;
      const next = new Map(prev);
      next.set(resourceId, { ...entry, timeSlots: [] });
      return next;
    });
  };

  // ---- Apply-banner --------------------------------------------------------

  // Trigger condition (documented):
  //   - selectedMap.size >= 2
  //   - banner not dismissed
  //   - canonical member's first slot is non-empty
  //   - at least one OTHER selected member's first slot != canonical first slot
  const { canonicalEntry, applyBannerVisible } = useMemo(() => {
    if (selectedMap.size < 2 || bannerDismissed || !hasUserEditedSlot) {
      return { canonicalEntry: null, applyBannerVisible: false };
    }
    const entries = Array.from(selectedMap.values());
    const fallbackEntry = entries[0];
    const activeEntry = activeMemberId
      ? (selectedMap.get(activeMemberId) ?? fallbackEntry)
      : fallbackEntry;
    if (!activeEntry) {
      return { canonicalEntry: null, applyBannerVisible: false };
    }
    const activeFirstSlot = activeEntry.timeSlots[0];
    if (!slotIsNonEmpty(activeFirstSlot)) {
      return { canonicalEntry: null, applyBannerVisible: false };
    }
    // After slotIsNonEmpty narrows truthiness above, activeFirstSlot is
    // guaranteed defined. Capture it to a non-undefined local so the .some()
    // closure can reference it without re-narrowing.
    const referenceSlot: TimeSlotDraft = activeFirstSlot;
    const hasMismatch = entries.some((entry) => {
      if (entry.resource.mapping_id === activeEntry.resource.mapping_id) {
        return false;
      }
      // Locked inactive members are excluded — the banner can't apply to them,
      // so their divergent time must not offer it (DD-5433).
      if (entry.isInactive) return false;
      const otherFirst = entry.timeSlots[0];
      if (!otherFirst) return true;
      return !slotMatches(otherFirst, referenceSlot);
    });
    return {
      canonicalEntry: activeEntry,
      applyBannerVisible: hasMismatch,
    };
  }, [selectedMap, bannerDismissed, activeMemberId, hasUserEditedSlot]);

  const applySameTimeToAll = () => {
    if (!canonicalEntry) return;
    const sourceSlot = canonicalEntry.timeSlots[0];
    if (!sourceSlot) return;
    setSelectedMap((prev) => {
      const next = new Map<string, SelectedMemberEntry>();
      for (const [id, entry] of prev) {
        if (id === canonicalEntry.resource.mapping_id) {
          next.set(id, entry);
          continue;
        }
        // Inactive members are locked — never overwrite their schedule with
        // the applied time (DD-5433).
        if (entry.isInactive) {
          next.set(id, entry);
          continue;
        }
        // Replace ONLY the first slot (locked: "Copy selected[0].timeSlots[0]
        // ... to timeSlots[0] of every active newly-selected member").
        // Other slots on that member are preserved.
        const restSlots =
          entry.timeSlots.length > 1 ? entry.timeSlots.slice(1) : [];
        next.set(id, {
          ...entry,
          timeSlots: [
            {
              slotUuid: uuidv4(),
              scheduleId: entry.timeSlots[0]?.scheduleId ?? null,
              startIso: sourceSlot.startIso,
              endIso: sourceSlot.endIso,
              error: { start: null, end: null },
            },
            ...restSlots,
          ],
        });
      }
      return next;
    });
    setBannerDismissed(true);
  };

  // ---- CTA enable rule -----------------------------------------------------
  // Save enabled iff: the selection differs from the hydrated baseline (no-op
  // edits stay disabled), no validation errors, not readOnly, and no in-flight
  // mutation. NOTE: we intentionally do NOT gate on `selectedMap.size === 0` —
  // opening with pre-assigned members and removing ALL of them is a legitimate
  // change (clear all assignees), and `isDirty` already keeps a fresh empty
  // drawer disabled (empty selection == empty baseline → not dirty).
  const currentSignature = selectionSignature(selectedMap);
  const isDirty = currentSignature !== baselineSignatureRef.current;
  // Reveal duplicate errors only while the selection is unchanged since the
  // Save attempt that flagged them — any edit changes the signature and hides
  // them (see duplicateAttemptSignature). Duplicates do NOT gate the button:
  // Save stays clickable so the attempt can surface them.
  const showDuplicateErrors =
    duplicateAttemptSignature !== null &&
    duplicateAttemptSignature === currentSignature;
  const isPrimaryDisabled =
    readOnly ||
    !isDirty ||
    isAnyTimeRowInvalid ||
    isEscalateMutationLoading ||
    isAssignSaving;

  // Submit handler.
  // Escalate mode: build EscalateTicketInput from selectedMap (already-escalated
  //   members are NEVER inserted into selectedMap so they are naturally
  //   excluded from `members[]`), call mutateAsync, fire UI-layer toast on
  //   success/error. Cache invalidation is owned by the hook — DO NOT
  //   re-invalidate here. Drawer closes on success, stays open on error
  //   (selections preserved because state isn't reset).
  // Assign mode: build OnSubmitReturnType, call onSave callback,
  //   close. Existing host integration handles assignment.
  const handlePrimaryButtonClick = async () => {
    if (readOnly) return;

    // Save-time duplicate gate: reveal the inline "Duplicate schedule" errors
    // and block submission. Capturing the current signature keeps them visible
    // until the user edits the selection (which changes the signature).
    if (hasAnyDuplicateSlots(selectedMap)) {
      setDuplicateAttemptSignature(currentSignature);
      return;
    }

    if (mode === "assign") {
      const payload = buildAssignPayload(selectedMap);
      // onSave may be sync (create-ticket → Jotai atom) or async (open-ticket →
      // create/schedule API). Await either; close only on success. If it throws,
      // keep the drawer open with the selection intact — the host owns surfacing
      // the error (toast), so we don't double-report here.
      try {
        setIsAssignSaving(true);
        await onSave?.(payload);
        onClose();
      } catch {
        // Host already surfaced the failure; preserve selection (no reset).
      } finally {
        setIsAssignSaving(false);
      }
      return;
    }

    // escalate mode
    if (!ticketId) return;
    const payload = buildEscalatePayload(selectedMap, "");
    if (payload.members.length === 0) return;

    try {
      const data = await escalateTicketAsync({ ticketId, payload });
      toast.success("Ticket escalated successfully");
      onEscalateSuccess?.(data);
      onClose();
    } catch (error: unknown) {
      const backendMessage =
        error instanceof Error && error.message ? error.message : null;
      toast.error(backendMessage ?? "Ticket escalation failed.");
      // Drawer stays open — selections preserved (state not reset).
    }
  };

  // ---- Renderers ----------------------------------------------------------

  // No-op handlers for already-escalated cards. Interaction is suppressed via
  // `readOnly={true}` + `disabledSelected={true}` on the card itself (checkbox
  // disabled, pickers read-only, no hover affordances).
  const noop = () => undefined;

  // Disabled-selected card for an already-escalated member (escalate mode) —
  // part of the pinned-top group, never interactive.
  const renderEscalatedCard = (entry: SelectedMemberEntry) => (
    <SelectedMemberCard
      key={`already-${entry.resource.mapping_id}`}
      entry={entry}
      readOnly
      disabledSelected
      showTimeRows={mode === "escalate"}
      onDeselect={noop}
      onToggleSchedule={noop}
      onChangeStart={noop}
      onChangeEnd={noop}
      onAddSchedule={noop}
      onDelete={noop}
    />
  );

  // Interactive expanded card for a currently-selected member — rendered IN
  // PLACE (at the member's own list position), not hoisted to the top.
  const renderSelectedCard = (entry: SelectedMemberEntry) => {
    // A locked card — a closed-ticket read-only drawer OR an inactive assignee
    // — renders exactly like an already-escalated card: muted content, grey
    // border + check-circle, no add/delete, read-only pickers, existing
    // schedule rows still visible. `inactive` additionally shows the
    // "Inactive technician" tooltip (DD-5433 / DD-5435).
    const memberInactive = !!entry.isInactive;
    const locked = readOnly || memberInactive;
    return (
      <SelectedMemberCard
        key={entry.resource.mapping_id}
        entry={entry}
        readOnly={locked}
        disabledSelected={locked}
        inactive={memberInactive}
        showTimeRows={schedulingEnabled}
        showDuplicateErrors={showDuplicateErrors}
        onDeselect={() => toggleSelection(entry.resource)}
        onToggleSchedule={() =>
          entry.timeSlots.length > 0
            ? clearSlots(entry.resource.mapping_id)
            : addSlot(entry.resource.mapping_id)
        }
        onChangeStart={(slotIndex, iso) =>
          updateSlot(entry.resource.mapping_id, slotIndex, {
            startIso: iso || null,
          })
        }
        onChangeEnd={(slotIndex, iso) =>
          updateSlot(entry.resource.mapping_id, slotIndex, {
            endIso: iso || null,
          })
        }
        onAddSchedule={() => addSlot(entry.resource.mapping_id)}
        onDelete={(slotIndex) =>
          removeSlot(entry.resource.mapping_id, slotIndex)
        }
      />
    );
  };

  // Compact clickable row for a member that is not currently selected. In
  // read-only mode (closed-but-prior path) NO row is interactive.
  const renderUnselectedRow = (resource: ResourceRow) => (
    <ResourceRowItem
      key={resource.mapping_id}
      resource={resource}
      isSelected={false}
      isDisabledSelected={false}
      schedulingEnabled={schedulingEnabled}
      onToggle={readOnly ? undefined : () => toggleSelection(resource)}
    />
  );

  // Render one member by its CURRENT selection state — selected → expanded
  // card, otherwise → compact row. Shared by the pinned-top group and the
  // in-position list so toggling selection swaps the row's look WITHOUT moving
  // it.
  const renderMemberRow = (resource: ResourceRow) => {
    const entry = selectedMap.get(resource.mapping_id);
    return entry ? renderSelectedCard(entry) : renderUnselectedRow(resource);
  };

  // Pinned-top group — the members already selected/escalated from the API when
  // the drawer opened (frozen at open, see the initialSelected* refs). They
  // stay at the top for the whole session even when unselected.
  //   - escalate mode → the already-escalated (disabled) members.
  //   - assign mode   → the initially-hydrated assignees; a still-selected one
  //     renders as an expanded card, an unselected one as a compact row (from
  //     the frozen snapshot, so it shows even if filtered out by search).
  const renderPinnedGroup = () => {
    if (mode === "escalate") {
      return alreadyEscalatedEntries.map(renderEscalatedCard);
    }
    return initialSelectedOrderRef.current.map((id) => {
      const entry = selectedMap.get(id);
      if (entry) return renderSelectedCard(entry);
      const snapshot = initialSelectedSnapshotRef.current.get(id);
      return snapshot ? renderUnselectedRow(snapshot) : null;
    });
  };

  // Searchable list below the pinned group — every fetched resource except the
  // ones already shown in the pinned group (frozen set) and already-escalated
  // members, each rendered in place by its selection state.
  const renderSearchableList = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}>
          <CircularLoader size={24} aria-label="Loading team members" />
        </Box>
      );
    }
    if (isError) {
      return <EmptyState message="Couldn't load team members." />;
    }
    // Exclude members already shown in the pinned group above — the frozen
    // initial-selection set (assign) and the already-escalated set (escalate).
    const isPinned = (id: string) =>
      initialSelectedIdsRef.current.has(id) || alreadyEscalatedSet.has(id);
    const rows = resourceList.filter((r) => !isPinned(r.mapping_id));
    if (rows.length === 0) {
      // Suppress the empty state while the pinned group still shows members
      // above (e.g. a search that matches nothing new).
      const hasPinned =
        mode === "escalate"
          ? alreadyEscalatedEntries.length > 0
          : initialSelectedOrderRef.current.length > 0;
      return hasPinned ? null : <EmptyState message="No team members found" />;
    }
    return rows.map(renderMemberRow);
  };

  // One continuous list — pinned-top group first, then the searchable list. No
  // separating border/section: selected members simply sort to the top.
  const renderBody = () => (
    <Stack sx={{ flex: 1, overflowY: "auto" }}>
      <Stack sx={{ p: "8px 16px", gap: "12px" }}>
        {renderPinnedGroup()}
        {renderSearchableList()}
      </Stack>
    </Stack>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      keepOriginalCasing
      drawerStyle={{ width: "680px" }}
      showHeaderCloseBtn
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onPrimaryButtonClick={handlePrimaryButtonClick}
      onSecondaryButtonClick={onClose}
      isPrimaryButtonDisabled={isPrimaryDisabled}
      isLoading={isEscalateMutationLoading || isAssignSaving}
    >
      <Stack sx={{ height: "100%", overflow: "hidden" }}>
        <Box sx={{ p: "16px" }}>
          <SearchInput
            fullWidth
            name="search"
            placeholder="Search team members"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>
        {schedulingEnabled && (
          <ApplySameTimeBanner
            visible={applyBannerVisible}
            onApply={applySameTimeToAll}
            onCancel={() => setBannerDismissed(true)}
          />
        )}
        {renderBody()}
      </Stack>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Selected-member card — wraps the shared MemberRowHeader + 1..N MemberTimeRows
// header-level AddScheduleIcon adds the first / further time rows. Time rows
// render only once added (selection now starts with zero rows).
// ---------------------------------------------------------------------------

interface SelectedMemberCardProps {
  entry: SelectedMemberEntry;
  readOnly: boolean;
  // When true the card renders in the "disabled-selected" visual state used
  // for already-escalated members: neutral card border (not primary), all
  // content muted at 50% opacity, non-interactive circle, no hover reveal of
  // time-row affordances. Must be paired with `readOnly={true}`.
  disabledSelected?: boolean;
  // Assign mode: this member's tech is inactive. Renders the same locked/muted
  // visual as disabledSelected, plus an "Inactive technician" tooltip on the
  // header explaining why it can't be edited (DD-5433).
  inactive?: boolean;
  showTimeRows: boolean;
  // Reveal inline "Duplicate schedule" errors. Driven by the drawer's save-time
  // duplicate gate — false during normal editing so a fresh add never flashes
  // one.
  showDuplicateErrors?: boolean;
  onDeselect: () => void;
  onToggleSchedule: () => void;
  onChangeStart: (slotIndex: number, iso: string) => void;
  onChangeEnd: (slotIndex: number, iso: string) => void;
  // Card-level "+" affordance — appends a NEW schedule row (seeded with the
  // resource's current time, same as the header's first-add). Replaces the old
  // per-row copy/duplicate icon.
  onAddSchedule: () => void;
  onDelete: (slotIndex: number) => void;
}

const SelectedMemberCard = ({
  entry,
  readOnly,
  disabledSelected = false,
  inactive = false,
  showTimeRows,
  showDuplicateErrors = false,
  onDeselect,
  onToggleSchedule,
  onChangeStart,
  onChangeEnd,
  onAddSchedule,
  onDelete,
}: SelectedMemberCardProps) => {
  const theme = useTheme();
  const fullName = `${emptyStringHandler(
    entry.resource.first_name,
  )} ${emptyStringHandler(entry.resource.last_name)}`.trim();

  // Validate this member's slots together. Range checks are always live; the
  // cross-slot duplicate check is gated on `showDuplicateErrors` so duplicates
  // surface only after a Save attempt (single source of truth with the drawer's
  // save-time gate).
  const slotValidations = validateMemberSlots(
    entry.timeSlots,
    showDuplicateErrors,
  );

  // Click-anywhere-on-the-header deselects (parity with the available row's
  // click-to-select). Scoped to the HEADER only — the time rows below are
  // interactive and must not deselect. The inner action IconButtons
  // (AddSchedule / CheckCircle) stopPropagation so they don't double-fire.
  const handleHeaderClick = readOnly ? undefined : onDeselect;
  const handleHeaderKeyDown = handleHeaderClick
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          handleHeaderClick();
        }
      }
    : undefined;

  const header = (
    <Box
      onClick={handleHeaderClick}
      role={handleHeaderClick ? "button" : undefined}
      tabIndex={handleHeaderClick ? 0 : -1}
      onKeyDown={handleHeaderKeyDown}
      aria-label={handleHeaderClick ? `Deselect ${fullName}` : undefined}
      sx={{ cursor: handleHeaderClick ? "pointer" : "default" }}
    >
      <MemberRowHeader
        resource={entry.resource}
        fullName={fullName}
        isSelected
        schedulingEnabled={showTimeRows}
        hasSchedules={entry.timeSlots.length > 0}
        muted={disabledSelected}
        onToggleSchedule={readOnly ? undefined : onToggleSchedule}
        onDeselect={readOnly ? undefined : onDeselect}
      />
    </Box>
  );

  return (
    <Stack
      sx={{
        p: "8px 16px",
        // Locked cards (already-escalated, inactive, or a closed-ticket
        // read-only drawer — all pass disabledSelected) use the neutral grey
        // border, same token as the disabled check-circle, instead of the
        // active primary outline.
        border: `1px solid ${disabledSelected ? theme.palette.darkBg.low : theme.palette.primary.main}`,
        borderRadius: "8px",
        gap: "8px",
        boxShadow: "0px 0px 4px 0px #00000029, 0px 0px 2px 0px #00000014",
        // Selected cards keep work badges visible (no hover gate).
        "& .member-row__meta": { opacity: 1 },
        // CSS-only hover reveal of the time-row delete icon — scoped to the
        // whole card. Disabled for any locked card (already-escalated,
        // inactive, or read-only — all pass disabledSelected); MemberTimeRow
        // skips those affordances when readOnly anyway, so nothing to reveal.
        ...(disabledSelected
          ? {}
          : {
              "& .time-row__icons": {
                opacity: 0,
                transition: "opacity 0.15s ease",
              },
              "&:hover .time-row__icons, &:focus-within .time-row__icons": {
                opacity: 1,
              },
            }),
      }}
    >
      {inactive ? (
        <Tooltip title="Inactive technician" disableSentenceCase>
          {header}
        </Tooltip>
      ) : (
        header
      )}
      {showTimeRows && entry.timeSlots.length > 0 && (
        <Stack sx={{ gap: "8px" }}>
          {entry.timeSlots.map((slot, slotIndex) => (
            <MemberTimeRow
              key={slot.slotUuid}
              resourceId={entry.resource.mapping_id}
              slotIndex={slotIndex}
              slot={slot}
              validation={
                slotValidations[slotIndex] ?? {
                  isValid: true,
                  errorMessage: null,
                }
              }
              readOnly={readOnly}
              showHoverAffordances={!readOnly}
              onChangeStart={(iso) => onChangeStart(slotIndex, iso)}
              onChangeEnd={(iso) => onChangeEnd(slotIndex, iso)}
              onDelete={() => onDelete(slotIndex)}
            />
          ))}
          {/* Card-level "+" — appends a NEW schedule row. Replaces the old
              per-row copy/duplicate icon (which read as "duplicate this slot"
              when the intent is "add another slot"). */}
          {!readOnly && <AddScheduleButton onClick={onAddSchedule} />}
        </Stack>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Available-list row — clickable card wrapping the shared MemberRowHeader.
//
// selection happens by clicking anywhere on the row. Work badges reveal on hover; the action icons reserve width but stay
// hidden until a row becomes selected (selected rows move to the Selected
// section as SelectedMemberCard, so `isSelected` is effectively always false
// here — the hidden icons just keep column width in parity).
// ---------------------------------------------------------------------------

interface ResourceRowItemProps {
  resource: ResourceRow;
  isSelected: boolean;
  isDisabledSelected: boolean;
  schedulingEnabled: boolean;
  onToggle?: () => void;
}

const ResourceRowItem = ({
  resource,
  isSelected,
  isDisabledSelected,
  schedulingEnabled,
  onToggle,
}: ResourceRowItemProps) => {
  const theme = useTheme();
  const fullName = `${emptyStringHandler(
    resource.first_name,
  )} ${emptyStringHandler(resource.last_name)}`.trim();

  // Interactive iff there's a toggle handler AND the row isn't a disabled
  // pre-selected one. In a read-only drawer (closed ticket) `onToggle` is
  // undefined → the row is inert: no pointer, no hover border/badge reveal.
  const interactive = !!onToggle && !isDisabledSelected;

  const borderColor =
    !isDisabledSelected && isSelected
      ? theme.palette.primary.main
      : theme.palette.neutral["005"];

  const handleKeyDown = onToggle
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onToggle();
        }
      }
    : undefined;

  return (
    <Stack
      onClick={onToggle}
      role={onToggle ? "button" : undefined}
      tabIndex={onToggle ? 0 : -1}
      onKeyDown={handleKeyDown}
      aria-disabled={isDisabledSelected || undefined}
      sx={{
        p: "8px 16px",
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        boxShadow: "0px 0px 4px 0px #00000029, 0px 0px 2px 0px #00000014",
        cursor: interactive ? "pointer" : "default",
        // Work badges hidden at rest, revealed on hover — only for interactive
        // rows. Inert rows (disabled or read-only) get no hover affordances and
        // keep the badges statically visible.
        ...(interactive
          ? {
              transition: "border-color 0.2s ease",
              "& .member-row__meta": {
                opacity: 0,
                transition: "opacity 0.15s ease",
              },
              "&:hover": { borderColor: theme.palette.primary.main },
              "&:hover .member-row__meta, &:focus-within .member-row__meta": {
                opacity: 1,
              },
            }
          : { "& .member-row__meta": { opacity: 1 } }),
      }}
    >
      <MemberRowHeader
        resource={resource}
        fullName={fullName}
        isSelected={isSelected}
        schedulingEnabled={schedulingEnabled}
        hasSchedules={false}
        muted={isDisabledSelected}
      />
    </Stack>
  );
};
